import { map } from 'rxjs/operators';
import { inject, Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import * as _ from 'lodash';
import { environment } from '../../../environments/environment';
import { AverageConsistency, Stable, StableMetric } from '../_models/stable';
import { MapService } from './map.service';
import { AnimalMovement } from '../_models/animalMovement';
import { StaticMapProxyService } from './static-map-proxy.service';

@Injectable()
export class StableService {
  // Services
  http = inject(HttpClient);
  injector = inject(Injector);

  private apiBaseUrl = environment.apiBaseUrl;
  stableList: Stable[];
  stableList$: BehaviorSubject<Array<Stable>> = new BehaviorSubject<Array<Stable>>([]);
  currentStable$: BehaviorSubject<Stable> = new BehaviorSubject<Stable>(null);

  // Services
  private staticMapsProxyService = inject(StaticMapProxyService);

  constructor() {}

  getStables(): Observable<Stable[]> {
    return this.http.get<Stable[]>(this.apiBaseUrl + 'api/private/v1/stables').pipe(
      map((response) => {
        this.stableList = this.decodeGeoJsonFields(response);
        this.stableList$.next(this.decodeGeoJsonFields(response));
        return response;
      })
    );
  }

  async updateStable(id: number): Promise<Stable> {
    const stable: Stable = await firstValueFrom(
      this.http.get(this.apiBaseUrl + 'api/private/v1/stables/' + id)
    );

    if (!stable) {
      return new Error('Stable not found');
    }

    const currentList = this.stableList$.getValue();

    const index = currentList.findIndex((s) => s.id === stable.id);

    if (index > -1) {
      currentList[index] = stable;
    } else {
      currentList.push(stable);
    }

    this.stableList$.next([...currentList]);

    return stable;
  }

  getStableByIdAndCompanyId(id: number, companyId: number): Observable<Stable> {
    return this.http
      .get(this.apiBaseUrl + 'api/private/v1/stables/' + id + '?companyIds=' + companyId)
      .pipe(map((response) => response));
  }

  saveStable(stableItem): Observable<Stable> {
    const stable = { ...stableItem };
    delete stable.lastUpdated;
    delete stable.dateCreated;

    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    stable.companyId = companySelected ? companySelected.id : null;

    if (stable.id) {
      if (stable.group) {
        if (stable.group !== 'null') {
          const idgroup: any = {
            id: stable.group.id,
          };
          stable.group = idgroup;
        } else {
          stable.group = null;
        }
      } else {
        stable.group = null;
      }
      delete stable.stableBoxes;
      delete stable.landRegistries;
      delete stable.numberOfBoxes;
      delete stable.polygon;

      return this.http
        .put<Stable>(this.apiBaseUrl + 'api/private/v1/stables/' + stable.id, stable)
        .pipe(map((response) => response));
    } else {
      return this.http
        .post<Stable>(this.apiBaseUrl + 'api/private/v1/stables/', stable)
        .pipe(map((response) => response));
    }
  }

  deleteStable(stable: Stable): Observable<Stable> {
    return this.http.delete(this.apiBaseUrl + 'api/private/v1/stables/' + stable.id).pipe(
      map((response) => {
        return { id: stable.id } as Stable;
      })
    );
  }

  private decodeGeoJsonFields(fields) {
    _.each(fields, (field) => {
      this.decodeGeoJsonField(field);
    });
    return fields;
  }

  private decodeGeoJsonField(stable) {
    const mapService = this.injector.get(MapService);
    const geoJsonTmp: any = this.getGeoJsonFeatureCollection();
    geoJsonTmp.features.push(this.getGeoJsonPolygonFeature());
    geoJsonTmp.features[0]['geometry'] = stable.polygon;
    stable.area = geoJsonTmp;
    stable.staticMapUrl = this.staticMapsProxyService.getStaticMapUrl(
      stable.area.features,
      '0xFF0000'
    );
    return stable;
  }

  getLocalStableByID(id) {
    const stable = this.stableList$.value?.find((s) => s.id == id);
    if (stable != null) {
      return stable;
    }
    return null;
  }

  public getGeoJsonFeatureCollection() {
    return {
      type: 'FeatureCollection',
      features: [],
    };
  }

  public getGeoJsonPolygonFeature() {
    return {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [[]],
      },
    };
  }

  getStableIdsByGroupId(group) {
    const ids = [];
    this.stableList$.subscribe((fields) => {
      fields.forEach((i) => {
        if (i.group && i.group.id == group) {
          ids.push(i.id);
        }
      });
    });
    return ids;
  }

  getProductionOrientationColorMap(): Map<string, string> {
    const map = new Map<string, string>();
    map.set('dairy_cattle', '#2596BE');
    map.set('beef_cattle', '#873E23');
    map.set('swine', '#F1B59D');
    map.set('ovine', '#BE2596');
    map.set('caprine', '#96BE25');
    map.set('chicken', '#BEA925');
    map.set('turkey', '#802EAF');
    map.set('laying_hen', '#E7224F');
    return map;
  }

  static getStableActivitiesColorMap(): Map<string, string> {
    const map = new Map<string, string>();
    map.set('pre_dipping_treatments', '#0ADEDF');
    map.set('post_dipping_treatment', '#03B346');
    map.set('livestock_care', '#2E8A8A');
    map.set('sanitary_vacuum', '#387D66');
    map.set('fertilisation', '#A9A257');
    map.set('barn_maintenance', '#C41172');
    map.set('litter_cleaning', '#26B865');
    map.set('ration', '#E65ED9');
    map.set('milking', '#D4D0FE');
    map.set('null', '#CC1919');
    return map;
  }

  getLivestockForecast(body: any): Observable<any> {
    let queryString = '?year=' + body.year.toString();
    queryString += '&supplyChain=' + body.supplyChain;
    if (body.stableId) {
      queryString += '&stableId=' + body.stableId;
    }
    if (body.type && body.type.length > 0) {
      body.type.forEach((type) => {
        queryString += '&type=' + type;
      });
    }

    return this.http.get(this.apiBaseUrl + 'api/private/v1/livestockForecast' + queryString).pipe(
      map((response) => {
        return response;
      })
    );
  }

  saveLivestockForecast(body): Observable<any> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    body.companyId = companySelected ? companySelected.id : null;

    if (body.id) {
      return this.http.put(this.apiBaseUrl + 'api/private/v1/livestockForecast', body).pipe(
        map((response) => {
          return response;
        })
      );
    } else {
      return this.http.post(this.apiBaseUrl + 'api/private/v1/livestockForecast', body).pipe(
        map((response) => {
          return response;
        })
      );
    }
  }

  getStableMetrics(metrics: string[] = []): Observable<StableMetric[]> {
    const searchParams = new URLSearchParams();

    metrics.forEach((m) => searchParams.append('kpis', m));

    return this.http
      .get<
        StableMetric[]
      >(this.apiBaseUrl + 'api/private/v2/livestock/stableMetrics?' + searchParams.toString())
      .pipe(map((response) => response));
  }

  getStableAvgConsistency(stableId: number, year: number): Observable<AverageConsistency> {
    return this.http
      .get<AverageConsistency>(
        this.apiBaseUrl + `api/private/v2/livestock/avgConsistency/${stableId}?year=${year}`
      )
      .pipe(map((response) => response));
  }

  async createStableDetection(detection: AnimalMovement[]): Promise<AnimalMovement[]> {
    return await firstValueFrom(
      this.http.post<AnimalMovement[]>(
        `${this.apiBaseUrl}api/private/v2/livestock/animalMovement/bulk`,
        detection
      )
    );
  }
}
